import { useState, useEffect, useRef } from 'react';
import './index.css';

/* ─────────────────────────────────────────
   TRANSLATIONS
───────────────────────────────────────── */
const T = {
  en: {
    navLinks: ['Problem', 'Features', 'How It Works', 'Reviews'],
    navCta: 'Download Now',
    heroBadge: '🇮🇳 Now Available Across India',
    heroTitle1: 'No more walking to',
    heroTitle2: 'check the menu.',
    heroTitle3: 'It comes to you.',
    heroDesc: 'SwaadIndia brings the daily mess menu straight to your phone. For students, bachelors, and the mess owners who feed them — across India.',
    heroCta1: '▶ Get on Play Store',
    heroCta2: '✦ See Features',
    heroTrust: '10,000+ users across India',
    probTag: '01 — The Problem',
    probHeading: '"Aaj mess mein kya hai?"',
    probSub: "Every student in a new city faces this daily. You walk to the mess. Sometimes it's closed. Sometimes you don't like what's cooking. Time wasted.",
    probs: [
      { e: '🚶‍♂️', title: 'No Menu Visibility', desc: "You have zero idea what's being cooked until you physically go there every single day.", color: 'var(--coral)' },
      { e: '❓', title: 'Is It Even Open?', desc: "No way to know if serving mode is on, off, or if there's a holiday. Pure guesswork.", color: 'var(--amber)' },
      { e: '📋', title: 'Manual Attendance', desc: 'Paper registers, disputes over remaining meals, zero accountability for anyone.', color: 'var(--violet)' },
    ],
    probSolve: 'SwaadIndia solves all three.',
    featTag: '02 — Features',
    featHeading: 'One app. Built for two worlds.',
    featDesc: 'Students get their menu. Vendors get their customers. Everyone wins.',
    tabUser: '🎓 For Students',
    tabVendor: '🏪 For Vendors',
    userFeats: [
      { i: '📍', t: 'Find Nearby Messes', d: 'Search by area, filter by Veg / Non-Veg. Discover every mess in your locality — without stepping out.', c: 'var(--coral)' },
      { i: '🍽', t: "Today's Menu, Live", d: "See exactly what's being cooked before you leave the house. Real-time updates from your mess.", c: 'var(--amber)' },
      { i: '💳', t: 'Flexible Subscriptions', d: "Monthly meal plans that flex with your life. Pause when you travel. Resume when you're back.", c: 'var(--violet)' },
      { i: '📱', t: 'QR Attendance Check-in', d: 'Scan QR at mealtime. Your attendance is marked. Remaining meals update instantly.', c: 'var(--mint)' },
    ],
    vendorFeats: [
      { i: '📢', t: 'Post Menu in Seconds', d: "Add today's rice plate or thali with one tap. All your customers are notified instantly.", c: 'var(--coral)' },
      { i: '⭐', t: 'Special Announcements', d: 'Festival menus, Sunday specials, holiday closures — broadcast to all subscribers at once.', c: 'var(--amber)' },
      { i: '🔲', t: 'Smart QR Attendance', d: 'Generate a unique QR. Customers scan it. Attendance logged. No disputes, no registers.', c: 'var(--violet)' },
      { i: '📊', t: 'Reports & Analytics', d: 'Daily inquiry count, attendance reports, subscription usage — know your business inside out.', c: 'var(--mint)' },
    ],
    qrTag: '03 — QR Attendance',
    qrHeading: 'Scan. Done.\nNo registers.',
    qrDesc: "The mess attendance problem is fully solved. Vendors generate a QR, students scan it — the entire cycle is automated.",
    qrFeats: [
      { t: 'Vendor generates unique QR per slot', d: 'Lunch QR. Dinner QR. Each session tracked independently.' },
      { t: 'Student scans at mealtime', d: 'Open app, point at QR, tap scan. Attendance marked in under 2 seconds.' },
      { t: 'Full report for vendor', d: "Who came, who didn't, how many meals remain — all visible in the Reports tab." },
      { t: 'Student tracks own meals', d: 'Days used, days remaining, subscription expiry — always visible on the home screen.' },
    ],
    qrStats: ['100%', 'Paperless', '0', 'Disputes', 'Real', 'Time Track', 'Auto', 'Reports'],
    stats: [
      { num: '10K+', label: 'Active Users', color: 'var(--coral)' },
      { num: '500+', label: 'Mess Vendors', color: 'var(--amber)' },
      { num: '29', label: 'States', color: 'var(--violet)' },
      { num: '4.5★', label: 'Play Store', color: 'var(--mint)' },
    ],
    howTag: '04 — How It Works',
    howHeading: 'Up and running\nin 4 steps.',
    tabUserHow: '👨‍🎓 Students',
    tabVendorHow: '🏪 Vendors',
    userSteps: [
      ['Download & Set Up', 'Get SwaadIndia on Play Store. Choose your city and food preference — Veg or Non-Veg.'],
      ['Find Your Mess', "Search nearby messes by area. See who's open and check the live menu right now."],
      ['Subscribe', 'Pick a monthly lunch or dinner plan. Pause anytime when you head home.'],
      ['Scan & Eat', 'Arrive at the mess, scan the QR code — attendance marked, meal counted. Simple.'],
    ],
    vendorSteps: [
      ['Register Your Mess', 'Sign up as a vendor. Add location, cuisine type, and pricing. Go live immediately.'],
      ["Post Today's Menu", "Add the day's thali every morning in one tap. All subscribed customers get notified."],
      ['QR at the Counter', 'Display your generated QR code. Customers scan at mealtime — zero manual work.'],
      ['Track & Grow', 'Monitor reports, manage subscriptions, watch your inquiry count rise.'],
    ],
    testiTag: '05 — Reviews',
    testiHeading: 'Real people.\nReal problems solved.',
    testis: [
      { name: 'Rahul Patil', role: 'Student, Pune', type: 'Student', text: '"Pehle roz mess tak jaata tha sirf menu dekhne. Ab ghar se hi pata chal jaata hai kya ban raha hai. Seriously life-changing app."', stars: 5 },
      { name: 'Suresh Bhai', role: 'Mess Owner, Nagpur', type: 'Vendor', text: '"QR attendance system ne meri life easy kar di. Ab koi dispute nahi, koi register nahi. Sab kuch automatic ho gaya hai."', stars: 5 },
      { name: 'Amit Kumar', role: 'Working Professional, Mumbai', type: 'Bachelor', text: '"Naya shehar tha, koi pehchaan nahi thi. SwaadIndia se area wise mess dhundha aur plan le liya. Pause feature is gold!"', stars: 5 },
      { name: 'Mangal Tai', role: 'Mess Owner, Pune', type: 'Vendor', text: '"App par menu daala toh khud naye customer aane lage. Inquiries badh gayi hain. Business grow ho raha hai bina extra kharche ke."', stars: 5 },
      { name: 'Karan Mehta', role: 'Engineering Student, Nashik', type: 'Student', text: '"Notification aati hai aaj ka menu dekho. Agar pasand nahi toh ghar khana bana lete hain. Petrol aur time dono bacha."', stars: 5 },
      { name: 'Prashant Yadav', role: 'Bachelor, Bengaluru', type: 'Bachelor', text: '"Jab ghar jaata hoon subscription pause karta hoon. Ek bhi meal waste nahi hota. Pehle yeh nahi hota tha kisi app mein."', stars: 5 },
    ],
    dlTag: 'Get the App',
    dlHeading: 'Your mess menu,\nalways one tap away.',
    dlDesc: 'Free to download. Thousands of messes. All of India.',
    dlNote: 'Currently available on Android · iOS coming soon',
    footerTagline: "India's mess & tiffin discovery platform.",
    footerCols: [
      { title: 'Product', links: ['For Students', 'For Vendors', 'How It Works', 'Download'] },
      { title: 'Company', links: ['About Us', 'Blog', 'Contact', 'Careers'] },
      { title: 'Legal', links: ['Privacy Policy', 'Terms of Use', 'Refund Policy'] },
    ],
    footerCopy: '© 2026 SwaadIndia. Made with ❤️ in India.',
    footerCities: 'Pune · Mumbai · Nagpur · Nashik · Bengaluru · Pan India',
  },
  hi: {
    navLinks: ['समस्या', 'फीचर्स', 'कैसे काम करे', 'रिव्यू'],
    navCta: 'डाउनलोड करें',
    heroBadge: '🇮🇳 अब पूरे भारत में उपलब्ध',
    heroTitle1: 'अब मेनू देखने',
    heroTitle2: 'मेस नहीं जाना।',
    heroTitle3: 'मेनू खुद आएगा।',
    heroDesc: 'SwaadIndia रोज़ाना मेस का मेनू सीधे आपके फोन पर लाता है। छात्रों, बैचलर्स और मेस मालिकों के लिए — पूरे भारत में।',
    heroCta1: '▶ Play Store पर पाएं',
    heroCta2: '✦ फीचर्स देखें',
    heroTrust: 'पूरे भारत में 10,000+ उपयोगकर्ता',
    probTag: '01 — समस्या',
    probHeading: '"आज मेस में क्या है?"',
    probSub: 'नए शहर में हर छात्र यह रोज़ झेलता है। मेस तक चलकर जाओ। कभी बंद मिलता है। कभी पसंद नहीं होता। समय बर्बाद।',
    probs: [
      { e: '🚶‍♂️', title: 'मेनू की जानकारी नहीं', desc: 'जब तक खुद नहीं जाते, पता नहीं चलता आज क्या बन रहा है।', color: 'var(--coral)' },
      { e: '❓', title: 'खुला भी है?', desc: 'पता नहीं मेस खुला है, बंद है या छुट्टी है। पूरा अंदाज़ा।', color: 'var(--amber)' },
      { e: '📋', title: 'मैन्युअल अटेंडेंस', desc: 'पेपर रजिस्टर, बचे मील पर झगड़े, किसी की जवाबदेही नहीं।', color: 'var(--violet)' },
    ],
    probSolve: 'SwaadIndia तीनों समस्याएं हल करता है।',
    featTag: '02 — फीचर्स',
    featHeading: 'एक ऐप। दो दुनिया।',
    featDesc: 'छात्रों को मेनू मिलता है। वेंडर को ग्राहक मिलते हैं।',
    tabUser: '🎓 छात्रों के लिए',
    tabVendor: '🏪 मेस मालकों के लिए',
    userFeats: [
      { i: '📍', t: 'नज़दीकी मेस खोजें', d: 'एरिया से सर्च करें, वेज/नॉन-वेज फ़िल्टर करें।', c: 'var(--coral)' },
      { i: '🍽', t: 'आज का मेनू, लाइव', d: 'घर से निकलने से पहले देखें क्या बन रहा है।', c: 'var(--amber)' },
      { i: '💳', t: 'लचीले सब्सक्रिप्शन', d: 'मासिक प्लान। यात्रा पर पॉज़ करें। वापस आने पर शुरू करें।', c: 'var(--violet)' },
      { i: '📱', t: 'QR अटेंडेंस चेक-इन', d: 'खाने पर QR स्कैन करें। अटेंडेंस दर्ज। बचे मील अपडेट।', c: 'var(--mint)' },
    ],
    vendorFeats: [
      { i: '📢', t: 'सेकंड में मेनू पोस्ट करें', d: 'एक टैप में आज की थाली जोड़ें। सभी ग्राहकों को तुरंत सूचना।', c: 'var(--coral)' },
      { i: '⭐', t: 'विशेष ऐलान', d: 'त्योहार मेनू, रविवार स्पेशल — सभी सब्सक्राइबर्स को एक साथ।', c: 'var(--amber)' },
      { i: '🔲', t: 'स्मार्ट QR अटेंडेंस', d: 'यूनिक QR जेनरेट करें। ग्राहक स्कैन करें। कोई झगड़ा नहीं।', c: 'var(--violet)' },
      { i: '📊', t: 'रिपोर्ट और एनालिटिक्स', d: 'रोज़ाना इंक्वायरी, अटेंडेंस रिपोर्ट — अपना बिज़नेस समझें।', c: 'var(--mint)' },
    ],
    qrTag: '03 — QR अटेंडेंस',
    qrHeading: 'स्कैन। हो गया।\nकोई रजिस्टर नहीं।',
    qrDesc: 'मेस अटेंडेंस की समस्या पूरी तरह हल। वेंडर QR बनाते हैं, छात्र स्कैन करते हैं — पूरा चक्र ऑटोमेटिक।',
    qrFeats: [
      { t: 'वेंडर प्रति स्लॉट यूनिक QR बनाता है', d: 'लंच QR। डिनर QR। हर सेशन अलग से ट्रैक।' },
      { t: 'छात्र खाने के समय स्कैन करता है', d: 'ऐप खोलें, QR पर कैमरा, टैप करें। 2 सेकंड में अटेंडेंस।' },
      { t: 'वेंडर के लिए पूरी रिपोर्ट', d: 'कौन आया, कौन नहीं, कितने मील बचे — सब रिपोर्ट टैब में।' },
      { t: 'छात्र खुद मील ट्रैक करता है', d: 'उपयोग किए दिन, बचे दिन, एक्सपायरी — होम स्क्रीन पर हमेशा।' },
    ],
    qrStats: ['100%', 'पेपरलेस', '0', 'विवाद नहीं', 'रियल', 'टाइम ट्रैक', 'ऑटो', 'रिपोर्ट'],
    stats: [
      { num: '10K+', label: 'सक्रिय यूजर', color: 'var(--coral)' },
      { num: '500+', label: 'मेस वेंडर', color: 'var(--amber)' },
      { num: '29', label: 'राज्य', color: 'var(--violet)' },
      { num: '4.5★', label: 'Play Store', color: 'var(--mint)' },
    ],
    howTag: '04 — कैसे काम करे',
    howHeading: '4 कदम में\nशुरू हो जाइए।',
    tabUserHow: '👨‍🎓 छात्र',
    tabVendorHow: '🏪 वेंडर',
    userSteps: [
      ['डाउनलोड करें', 'Play Store से SwaadIndia डाउनलोड करें। शहर और पसंद चुनें।'],
      ['मेस खोजें', 'एरिया से नज़दीकी मेस खोजें। लाइव मेनू देखें।'],
      ['सब्सक्राइब करें', 'मासिक लंच या डिनर प्लान चुनें। जब चाहें पॉज़ करें।'],
      ['स्कैन करें और खाएं', 'मेस पहुंचें, QR स्कैन करें — अटेंडेंस दर्ज।'],
    ],
    vendorSteps: [
      ['मेस रजिस्टर करें', 'वेंडर के रूप में साइन अप करें। लोकेशन और प्राइसिंग जोड़ें।'],
      ['आज का मेनू पोस्ट करें', 'हर सुबह एक टैप में थाली जोड़ें। ग्राहकों को नोटिफिकेशन।'],
      ['काउंटर पर QR लगाएं', 'अपना QR दिखाएं। ग्राहक स्कैन करें।'],
      ['ट्रैक करें और बढ़ें', 'रिपोर्ट देखें, सब्सक्रिप्शन मैनेज करें।'],
    ],
    testiTag: '05 — रिव्यू',
    testiHeading: 'असली लोग।\nअसली समस्या हल।',
    testis: [
      { name: 'राहुल पाटिल', role: 'छात्र, पुणे', type: 'छात्र', text: '"पहले रोज़ मेस तक जाता था सिर्फ मेनू देखने। अब घर से ही पता चल जाता है।"', stars: 5 },
      { name: 'सुरेश भाई', role: 'मेस मालिक, नागपुर', type: 'वेंडर', text: '"QR सिस्टम ने मेरी ज़िंदगी आसान कर दी। कोई झगड़ा नहीं, कोई रजिस्टर नहीं।"', stars: 5 },
      { name: 'अमित कुमार', role: 'प्रोफेशनल, मुंबई', type: 'बैचलर', text: '"नया शहर था। SwaadIndia से एरिया वाइज़ मेस ढूंढा। पॉज़ फीचर तो बेस्ट है!"', stars: 5 },
      { name: 'मंगल ताई', role: 'मेस मालिक, पुणे', type: 'वेंडर', text: '"ऐप पर मेनू डाला तो खुद नए ग्राहक आने लगे। बिज़नेस बढ़ रहा है।"', stars: 5 },
      { name: 'करण मेहता', role: 'इंजीनियरिंग छात्र, नाशिक', type: 'छात्र', text: '"नोटिफिकेशन से मेनू पता चलता है। पेट्रोल और समय दोनों बचे।"', stars: 5 },
      { name: 'प्रशांत यादव', role: 'बैचलर, बेंगलुरु', type: 'बैचलर', text: '"घर जाने पर पॉज़ करता हूं। एक भी मील वेस्ट नहीं होती।"', stars: 5 },
    ],
    dlTag: 'ऐप पाएं',
    dlHeading: 'आपका मेस मेनू,\nहमेशा एक टैप दूर।',
    dlDesc: 'मुफ्त डाउनलोड। हज़ारों मेस। पूरा भारत।',
    dlNote: 'अभी Android पर उपलब्ध · iOS जल्द आएगा',
    footerTagline: 'भारत का मेस और टिफिन डिस्कवरी प्लेटफॉर्म।',
    footerCols: [
      { title: 'प्रोडक्ट', links: ['छात्रों के लिए', 'वेंडर के लिए', 'कैसे काम करे', 'डाउनलोड'] },
      { title: 'कंपनी', links: ['हमारे बारे में', 'ब्लॉग', 'संपर्क', 'करियर'] },
      { title: 'कानूनी', links: ['गोपनीयता नीति', 'उपयोग शर्तें', 'रिफंड नीति'] },
    ],
    footerCopy: '© 2026 SwaadIndia। ❤️ के साथ भारत में बना।',
    footerCities: 'पुणे · मुंबई · नागपुर · नाशिक · बेंगलुरु · पूरे भारत में',
  },
  mr: {
    navLinks: ['समस्या', 'वैशिष्ट्ये', 'कसे काम करते', 'रिव्ह्यू'],
    navCta: 'डाउनलोड करा',
    heroBadge: '🇮🇳 आता संपूर्ण भारतात उपलब्ध',
    heroTitle1: 'आता मेनू पाहण्यासाठी',
    heroTitle2: 'मेसला जाण्याची गरज नाही।',
    heroTitle3: 'मेनू स्वतः येतो।',
    heroDesc: 'SwaadIndia रोजचे मेस मेनू थेट तुमच्या फोनवर आणते. विद्यार्थी, बॅचलर्स आणि मेस मालकांसाठी — संपूर्ण भारतात.',
    heroCta1: '▶ Play Store वर मिळवा',
    heroCta2: '✦ वैशिष्ट्ये पहा',
    heroTrust: 'भारतात 10,000+ वापरकर्ते',
    probTag: '01 — समस्या',
    probHeading: '"आज मेसमध्ये काय आहे?"',
    probSub: 'नवीन शहरातील प्रत्येक विद्यार्थी हे रोज अनुभवतो. मेसला चालत जा. कधी बंद असते. वेळ वाया.',
    probs: [
      { e: '🚶‍♂️', title: 'मेनूची माहिती नाही', desc: 'स्वतः जाऊन पाहिल्याशिवाय आज काय बनत आहे हे कळत नाही.', color: 'var(--coral)' },
      { e: '❓', title: 'उघडे आहे का?', desc: 'मेस उघडे आहे, बंद आहे, की सुट्टी आहे हे माहीत नाही.', color: 'var(--amber)' },
      { e: '📋', title: 'मॅन्युअल हजेरी', desc: 'कागदी रजिस्टर, उरलेल्या जेवणावर वाद, कोणाची जबाबदारी नाही.', color: 'var(--violet)' },
    ],
    probSolve: 'SwaadIndia तिन्ही समस्या सोडवते.',
    featTag: '02 — वैशिष्ट्ये',
    featHeading: 'एक ऐप. दोन जग.',
    featDesc: 'विद्यार्थ्यांना मेनू मिळतो. विक्रेत्यांना ग्राहक मिळतात.',
    tabUser: '🎓 विद्यार्थ्यांसाठी',
    tabVendor: '🏪 मेस मालकांसाठी',
    userFeats: [
      { i: '📍', t: 'जवळचे मेस शोधा', d: 'भागानुसार शोधा, शाकाहारी/मांसाहारी फिल्टर करा.', c: 'var(--coral)' },
      { i: '🍽', t: 'आजचा मेनू, थेट', d: 'घराबाहेर पडण्यापूर्वी आज काय बनत आहे पाहा.', c: 'var(--amber)' },
      { i: '💳', t: 'लवचिक सदस्यता', d: 'मासिक जेवण योजना. प्रवासात थांबवा. परत आल्यावर सुरू करा.', c: 'var(--violet)' },
      { i: '📱', t: 'QR हजेरी चेक-इन', d: 'जेवणाच्या वेळी QR स्कॅन करा. हजेरी नोंदली.', c: 'var(--mint)' },
    ],
    vendorFeats: [
      { i: '📢', t: 'सेकंदात मेनू पोस्ट करा', d: 'एका टॅपने आजची थाळी जोडा. सर्व ग्राहकांना लगेच सूचना.', c: 'var(--coral)' },
      { i: '⭐', t: 'विशेष जाहिराती', d: 'सणांचे मेनू, रविवार स्पेशल — सर्व सदस्यांना एकत्र.', c: 'var(--amber)' },
      { i: '🔲', t: 'स्मार्ट QR हजेरी', d: 'अद्वितीय QR तयार करा. ग्राहक स्कॅन करतात. कोणताही वाद नाही.', c: 'var(--violet)' },
      { i: '📊', t: 'अहवाल व विश्लेषण', d: 'रोजची चौकशी, हजेरी अहवाल — सर्व माहिती.', c: 'var(--mint)' },
    ],
    qrTag: '03 — QR हजेरी',
    qrHeading: 'स्कॅन. झाले.\nकोणताही रजिस्टर नाही.',
    qrDesc: 'मेस हजेरीची समस्या पूर्णपणे सुटली. विक्रेते QR बनवतात, विद्यार्थी स्कॅन करतात.',
    qrFeats: [
      { t: 'विक्रेता प्रति स्लॉट QR बनवतो', d: 'दुपारचे जेवण QR. रात्रीचे जेवण QR.' },
      { t: 'विद्यार्थी जेवणाच्या वेळी स्कॅन करतो', d: 'ऐप उघडा, QR वर कॅमेरा. 2 सेकंदात हजेरी.' },
      { t: 'विक्रेत्यासाठी संपूर्ण अहवाल', d: 'कोण आले, कोण नाही — सर्व अहवाल टॅबमध्ये.' },
      { t: 'विद्यार्थी स्वतःचे जेवण ट्रॅक करतो', d: 'वापरलेले दिवस, उरलेले — होम स्क्रीनवर नेहमी.' },
    ],
    qrStats: ['100%', 'कागदरहित', '0', 'वाद नाही', 'रिअल', 'टाइम ट्रॅक', 'आपोआप', 'अहवाल'],
    stats: [
      { num: '10K+', label: 'सक्रिय वापरकर्ते', color: 'var(--coral)' },
      { num: '500+', label: 'मेस विक्रेते', color: 'var(--amber)' },
      { num: '29', label: 'राज्ये', color: 'var(--violet)' },
      { num: '4.5★', label: 'Play Store', color: 'var(--mint)' },
    ],
    howTag: '04 — कसे काम करते',
    howHeading: '4 पायऱ्यांत\nसुरुवात करा.',
    tabUserHow: '👨‍🎓 विद्यार्थी',
    tabVendorHow: '🏪 विक्रेते',
    userSteps: [
      ['डाउनलोड करा', 'Play Store वरून SwaadIndia डाउनलोड करा. शहर आणि पसंती निवडा.'],
      ['मेस शोधा', 'भागानुसार जवळचे मेस शोधा. लाइव्ह मेनू पाहा.'],
      ['सदस्यता घ्या', 'मासिक दुपार किंवा रात्रीच्या जेवणाची योजना निवडा.'],
      ['स्कॅन करा आणि खा', 'मेसवर पोहोचा, QR स्कॅन करा — हजेरी नोंदली.'],
    ],
    vendorSteps: [
      ['मेस नोंदणी करा', 'विक्रेता म्हणून साइन अप करा. स्थान आणि किंमत जोडा.'],
      ['आजचा मेनू पोस्ट करा', 'दररोज सकाळी एका टॅपने थाळी जोडा.'],
      ['काउंटरवर QR लावा', 'तुमचा QR कोड दाखवा. ग्राहक स्कॅन करतात.'],
      ['ट्रॅक करा आणि वाढा', 'अहवाल पाहा, सदस्यता व्यवस्थापित करा.'],
    ],
    testiTag: '05 — रिव्ह्यू',
    testiHeading: 'खरे लोक.\nखरी समस्या सुटली.',
    testis: [
      { name: 'राहुल पाटील', role: 'विद्यार्थी, पुणे', type: 'विद्यार्थी', text: '"आधी रोज मेसला जायचो फक्त मेनू पाहायला. आता घरून कळतं काय बनतंय."', stars: 5 },
      { name: 'सुरेश भाऊ', role: 'मेस मालक, नागपूर', type: 'विक्रेता', text: '"QR प्रणालीने माझे जीवन सोपे केले. कोणताही वाद नाही, कोणताही रजिस्टर नाही."', stars: 5 },
      { name: 'अमित कुमार', role: 'व्यावसायिक, मुंबई', type: 'बॅचलर', text: '"नवीन शहर होते. SwaadIndia ने भागनिहाय मेस शोधण्यास मदत केली."', stars: 5 },
      { name: 'मंगल ताई', role: 'मेस मालक, पुणे', type: 'विक्रेता', text: '"ऐपवर मेनू टाकला तर स्वतः नवीन ग्राहक आले. व्यवसाय वाढतोय."', stars: 5 },
      { name: 'करण मेहता', role: 'इंजिनीअरिंग विद्यार्थी, नाशिक', type: 'विद्यार्थी', text: '"नोटिफिकेशनने मेनू कळतो. पेट्रोल आणि वेळ दोन्ही वाचले."', stars: 5 },
      { name: 'प्रशांत यादव', role: 'बॅचलर, बेंगळुरू', type: 'बॅचलर', text: '"घरी गेल्यावर पॉज करतो. एकही जेवण वाया जात नाही."', stars: 5 },
    ],
    dlTag: 'ऐप मिळवा',
    dlHeading: 'तुमचे मेस मेनू,\nनेहमी एका टॅपवर.',
    dlDesc: 'मोफत डाउनलोड. हजारो मेस. संपूर्ण भारत.',
    dlNote: 'सध्या Android वर उपलब्ध · iOS लवकरच',
    footerTagline: 'भारताचे मेस आणि टिफिन डिस्कव्हरी प्लॅटफॉर्म.',
    footerCols: [
      { title: 'उत्पादन', links: ['विद्यार्थ्यांसाठी', 'विक्रेत्यांसाठी', 'कसे काम करते', 'डाउनलोड'] },
      { title: 'कंपनी', links: ['आमच्याबद्दल', 'ब्लॉग', 'संपर्क', 'करिअर'] },
      { title: 'कायदेशीर', links: ['गोपनीयता धोरण', 'वापराच्या अटी', 'परतावा धोरण'] },
    ],
    footerCopy: '© 2026 SwaadIndia. ❤️ सह भारतात बनवले.',
    footerCities: 'पुणे · मुंबई · नागपूर · नाशिक · बेंगळुरू · संपूर्ण भारतात',
  },
};

/* ─────────────────────────────────────────
   HOOK: useReveal
───────────────────────────────────────── */
function useReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { el.classList.add('in'); obs.disconnect(); } },
      { threshold: 0.1 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return ref;
}

/* ─────────────────────────────────────────
   PHONE SCREENS
───────────────────────────────────────── */
function UserScreen() {
  return (
    <div>
      <div className="us-header">
        <div className="us-header-top">
          <div>
            <div className="us-welcome">Welcome</div>
            <div className="us-name">Rahul Sharma</div>
          </div>
          <div className="us-avatar">RS</div>
        </div>
        <div className="us-date">Tuesday, Mar 31, 2026</div>
      </div>
      <div className="us-body">
        <div className="us-search">🔍 Search area / Hotel / Mess...</div>
        <div className="us-filters">
          <div className="us-filter filter-city">📍 Pune ▾</div>
          <div className="us-filter filter-nonveg">Non-Veg</div>
          <div className="us-filter filter-veg">Pure Veg</div>
        </div>
        <div className="plan-card">
          <div className="plan-label">Active Plan</div>
          <div className="plan-name">Sharma Mess</div>
          <div className="plan-type-badge">LUNCH</div>
          <div className="plan-bar-bg"><div className="plan-bar-fill" /></div>
          <div className="plan-stats">
            <div className="plan-stat"><div className="ps-num">2</div><div className="ps-label">Used</div></div>
            <div className="ps-divider" />
            <div className="plan-stat"><div className="ps-num green">28</div><div className="ps-label">Left</div></div>
            <div className="ps-divider" />
            <div className="plan-stat"><div className="ps-num">30</div><div className="ps-label">Total</div></div>
          </div>
        </div>
        <div className="us-sec-label">Vendors Near You</div>
        <div className="vendor-card-mini">
          <div className="vcm-name">Sharma Mess</div>
          <div className="vcm-meta">🍽 Pure Veg · 📍 Wakad, Pune</div>
          <div className="vcm-badge badge-open">Dal Chawal, Sabji, Roti</div>
        </div>
        <div className="vendor-card-mini">
          <div className="vcm-name">Vendor3 Mess</div>
          <div className="vcm-meta">🍽 Veg/Non-Veg · 📍 Akurdi</div>
          <div className="vcm-badge badge-closed">Currently Closed</div>
        </div>
      </div>
    </div>
  );
}

function VendorScreen() {
  const gridItems = [
    ['🍽', 'Add Menu', 'var(--coral)'],
    ['⭐', 'Special', 'var(--amber)'],
    ['👥', 'Attendance', 'var(--violet)'],
    ['💳', 'Subscriptions', 'var(--mint)'],
    ['🕐', 'History', '#FF7043'],
    ['📊', 'Reports', '#29B6F6'],
  ];
  return (
    <div>
      <div className="vs-header">
        <div className="vs-header-top">
          <div>
            <div className="vs-welcome">Welcome,</div>
            <div className="vs-name">Sharma Mess</div>
          </div>
          <div className="vs-avatar">SM</div>
        </div>
      </div>
      <div className="vs-body">
        <div className="vs-inq-card">
          <div className="vs-inq-label">Interest Today</div>
          <div className="vs-inq-val">3 Inquiries</div>
        </div>
        <div className="vs-status-card">
          <div className="vs-status-row">
            <div>
              <div className="vs-status-title">Current Status</div>
              <div className="vs-status-date">Tue, 31 Mar 2026</div>
            </div>
            <div className="vs-toggle"><div className="vs-toggle-dot" /></div>
          </div>
          <div className="vs-mode">SERVING MODE — ON</div>
        </div>
        <div className="vs-grid">
          {gridItems.map(([icon, label, color], i) => (
            <div key={i} className="vs-grid-btn" style={{ borderLeft: `3px solid ${color}` }}>
              <div className="vs-grid-icon">{icon}</div>
              <div className="vs-grid-label">{label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function PhoneMock({ children, className = '' }) {
  return (
    <div className={className}>
      <div className="phone-outer">
        <div className="phone-screen-wrap">{children}</div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────
   NAVBAR
───────────────────────────────────────── */
function Navbar({ lang, setLang, t }) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', h);
    return () => window.removeEventListener('scroll', h);
  }, []);

  const sectionIds = ['problem', 'features', 'howitworks', 'reviews'];
  const scroll = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  return (
    <nav className={`navbar ${scrolled ? 'scrolled' : ''}`}>
      <div className="navbar-inner">
        <div className="navbar-logo" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
          <div className="logo-mark">🍽</div>
          <div className="logo-text">Swaad<span>India</span></div>
        </div>
        <ul className="nav-links">
          {t.navLinks.map((l, i) => (
            <li key={i} onClick={() => scroll(sectionIds[i])}>{l}</li>
          ))}
        </ul>
        <div className="nav-right">
          <div className="lang-btns">
            {['en', 'hi', 'mr'].map((l) => (
              <button key={l} className={`lang-btn ${lang === l ? 'active' : ''}`} onClick={() => setLang(l)}>
                {l === 'en' ? 'EN' : l === 'hi' ? 'हि' : 'म'}
              </button>
            ))}
          </div>
          <button className="nav-cta">{t.navCta}</button>
        </div>
      </div>
    </nav>
  );
}

/* ─────────────────────────────────────────
   HERO
───────────────────────────────────────── */
function Hero({ t }) {
  return (
    <section className="hero">
      <div className="hero-bg-orb" />
      <div className="hero-grid" />
      <div className="hero-left">
        <div className="hero-badge">{t.heroBadge}</div>
        <h1 className="hero-title">
          <span>{t.heroTitle1}</span><br />
          <span className="hero-title-muted">{t.heroTitle2}</span><br />
          <span className="hero-title-grad">{t.heroTitle3}</span>
        </h1>
        <p className="hero-desc">{t.heroDesc}</p>
        <div className="hero-actions">
          <button className="btn-primary">{t.heroCta1}</button>
          <button className="btn-secondary" onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}>
            {t.heroCta2}
          </button>
        </div>
        <div className="hero-trust">
          <div className="trust-avatars">
            {[['R', '#FF4D2E'], ['P', '#7C5CFC'], ['A', '#00DBA8'], ['K', '#FFAA00']].map(([l, c], i) => (
              <div key={i} className="trust-av" style={{ background: c }}>{l}</div>
            ))}
          </div>
          <div className="trust-text">{t.heroTrust}</div>
        </div>
      </div>
      <div className="hero-right">
        {/* Top notification */}
        <div className="notif-float top">
          <div className="notif-emoji">🍛</div>
          <div className="notif-title">Today's Menu Updated!</div>
          <div className="notif-sub">Sharma Mess · <span className="mint">Lunch 12pm</span></div>
        </div>
        {/* Back phone - vendor */}
        <PhoneMock className="phone-back-left">
          <VendorScreen />
        </PhoneMock>
        {/* Main phone - user */}
        <PhoneMock className="phone-main">
          <UserScreen />
        </PhoneMock>
        {/* Bottom notification */}
        <div className="notif-float bottom">
          <div className="notif-emoji">✅</div>
          <div className="notif-title">QR Scan Successful</div>
          <div className="notif-sub">Rahul · <span className="mint">Lunch Marked ✓</span></div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────
   MARQUEE
───────────────────────────────────────── */
function Marquee() {
  const items = ['🍛 Butter Chicken', '🫓 Masala Dosa', '🥘 Dal Makhani', '🍚 Biryani', '🧆 Chole Bhature', '🫕 Rajma Chawal', '🍲 Sambar Rice', '🥙 Vada Pav', '🍮 Khichdi', '🌯 Pav Bhaji'];
  const doubled = [...items, ...items];
  return (
    <div className="marquee-bar">
      <div className="marquee-track">
        {doubled.map((item, i) => <span key={i} className="marquee-item">{item}</span>)}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────
   PROBLEM
───────────────────────────────────────── */
function Problem({ t }) {
  const r1 = useReveal(), r2 = useReveal(), r3 = useReveal(), r4 = useReveal();
  return (
    <section id="problem" className="problem-section">
      <div className="problem-inner">
        <span ref={r1} className="reveal section-tag tag-coral">{t.probTag}</span>
        <h2 ref={r2} className="reveal section-heading large grad-coral">{t.probHeading}</h2>
        <p ref={r3} className="reveal section-desc" style={{ maxWidth: 560, marginBottom: '1rem' }}>{t.probSub}</p>
        <div ref={r4} className="reveal problem-grid">
          {t.probs.map((p, i) => (
            <div key={i} className="problem-card"
              onMouseEnter={e => { e.currentTarget.style.borderColor = p.color; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; }}>
              <div className="prob-big-num" style={{ color: p.color }}>{i + 1}</div>
              <span className="prob-emoji">{p.e}</span>
              <div className="prob-title">{p.title}</div>
              <div className="prob-desc">{p.desc}</div>
              <div className="prob-line" style={{ background: p.color }} />
            </div>
          ))}
        </div>
        <div className="solve-tag">✦ {t.probSolve}</div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────
   FEATURES
───────────────────────────────────────── */
function Features({ t }) {
  const [tab, setTab] = useState('user');
  const r1 = useReveal(), r2 = useReveal(), r3 = useReveal();
  const feats = tab === 'user' ? t.userFeats : t.vendorFeats;

  return (
    <section id="features" className="features-section">
      <div className="features-inner">
        <div className="features-top">
          <div ref={r1} className="reveal">
            <span className="section-tag tag-amber">{t.featTag}</span>
            <h2 className="section-heading">{t.featHeading}</h2>
            <p className="section-desc">{t.featDesc}</p>
            <div className="audience-tabs">
              <button className={`aud-tab ${tab === 'user' ? 'active' : ''}`} onClick={() => setTab('user')}>{t.tabUser}</button>
              <button className={`aud-tab ${tab === 'vendor' ? 'active' : ''}`} onClick={() => setTab('vendor')}>{t.tabVendor}</button>
            </div>
          </div>
          <div ref={r2} className="reveal phone-center">
            <PhoneMock className="phone-main" style={{}}>
              {tab === 'user' ? <UserScreen /> : <VendorScreen />}
            </PhoneMock>
          </div>
        </div>
        <div ref={r3} className="reveal features-grid">
          {feats.map((f, i) => (
            <div key={`${tab}-${i}`} className="feature-card"
              onMouseEnter={e => { e.currentTarget.style.borderColor = f.c; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; }}>
              <div className="feat-icon-box" style={{ background: `${f.c}18` }}>{f.i}</div>
              <div>
                <div className="feat-title">{f.t}</div>
                <div className="feat-desc">{f.d}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────
   QR SECTION
───────────────────────────────────────── */
function QRSection({ t }) {
  const r1 = useReveal(), r2 = useReveal();
  const qrDots = [[45,5],[57,5],[69,5],[81,5],[45,17],[69,17],[45,29],[57,29],[5,45],[17,45],[29,45],[45,45],[57,45],[69,45],[81,45],[93,45],[105,45],[117,45],[5,57],[29,57],[57,57],[81,57],[105,57],[5,69],[17,69],[45,69],[69,69],[81,69],[93,69],[117,69],[5,81],[29,81],[57,81],[69,81],[105,81],[45,93],[57,93],[69,93],[93,93],[117,93],[45,105],[81,105],[105,105],[45,117],[57,117],[69,117],[93,117],[117,117]];
  const qrStats = t.qrStats;

  return (
    <section id="qr" className="qr-section">
      <div className="qr-inner">
        <div ref={r1} className="reveal">
          <span className="section-tag tag-violet">{t.qrTag}</span>
          <h2 className="section-heading" style={{ whiteSpace: 'pre-line' }}>{t.qrHeading}</h2>
          <p className="section-desc" style={{ maxWidth: 460, marginBottom: '2rem' }}>{t.qrDesc}</p>
          <div className="qr-feat-list">
            {t.qrFeats.map((f, i) => (
              <div key={i} className="qr-feat-item">
                <div className="qr-dot" />
                <div>
                  <div className="qr-feat-title">{f.t}</div>
                  <div className="qr-feat-desc">{f.d}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div ref={r2} className="reveal qr-visual">
          <div className="qr-card">
            <div className="qr-card-top-bar" />
            <div className="qr-card-title">🔲 Smart QR Attendance</div>
            <div className="qr-img-box">
              <div className="qr-scan-line" />
              <svg viewBox="0 0 130 130" width="130" height="130" xmlns="http://www.w3.org/2000/svg">
                <rect x="5" y="5" width="30" height="30" fill="none" stroke="#7C5CFC" strokeWidth="3" rx="3"/>
                <rect x="9" y="9" width="22" height="22" fill="#7C5CFC" rx="2"/>
                <rect x="95" y="5" width="30" height="30" fill="none" stroke="#7C5CFC" strokeWidth="3" rx="3"/>
                <rect x="99" y="9" width="22" height="22" fill="#7C5CFC" rx="2"/>
                <rect x="5" y="95" width="30" height="30" fill="none" stroke="#7C5CFC" strokeWidth="3" rx="3"/>
                <rect x="9" y="99" width="22" height="22" fill="#7C5CFC" rx="2"/>
                {qrDots.map(([x, y], i) => <rect key={i} x={x} y={y} width="6" height="6" fill="#333" rx="1"/>)}
              </svg>
            </div>
            <div className="qr-stats-grid">
              {[[qrStats[0], qrStats[1]], [qrStats[2], qrStats[3]], [qrStats[4], qrStats[5]], [qrStats[6], qrStats[7]]].map(([n, l], i) => (
                <div key={i} className="qr-stat">
                  <div className="qr-stat-num">{n}</div>
                  <div className="qr-stat-label">{l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────
   STATS
───────────────────────────────────────── */
function Stats({ t }) {
  const ref = useReveal();
  return (
    <div className="stats-bar">
      <div ref={ref} className="reveal stats-inner">
        {t.stats.map((s, i) => (
          <div key={i} className="stat-item">
            <div className="stat-num" style={{ color: s.color }}>{s.num}</div>
            <div className="stat-label">{s.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────
   HOW IT WORKS
───────────────────────────────────────── */
function HowItWorks({ t }) {
  const [tab, setTab] = useState('user');
  const r1 = useReveal(), r2 = useReveal();
  const steps = tab === 'user' ? t.userSteps : t.vendorSteps;
  const colors = ['var(--coral)', 'var(--amber)', 'var(--violet)', 'var(--mint)'];

  return (
    <section id="howitworks" className="how-section">
      <div className="how-inner">
        <span ref={r1} className="reveal section-tag tag-mint">{t.howTag}</span>
        <h2 ref={r1} className="reveal section-heading" style={{ whiteSpace: 'pre-line' }}>{t.howHeading}</h2>
        <div className="how-tabs">
          <button className={`how-tab ${tab === 'user' ? 'active' : ''}`} onClick={() => setTab('user')}>{t.tabUserHow}</button>
          <button className={`how-tab ${tab === 'vendor' ? 'active' : ''}`} onClick={() => setTab('vendor')}>{t.tabVendorHow}</button>
        </div>
        <div ref={r2} className="reveal how-steps">
          <div className="how-connector" />
          {steps.map(([title, desc], i) => (
            <div key={`${tab}-${i}`} className="how-step">
              <div className="step-num-wrap" style={{ background: `${colors[i]}18`, border: `2px solid ${colors[i]}`, boxShadow: `0 6px 24px ${colors[i]}30` }}>
                <span className="step-num">{i + 1}</span>
              </div>
              <div className="step-title">{title}</div>
              <div className="step-desc">{desc}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────
   TESTIMONIALS
───────────────────────────────────────── */
function Testimonials({ t }) {
  const r1 = useReveal(), r2 = useReveal();
  const typeColors = {
    'Student': 'var(--coral)', 'Bachelor': 'var(--amber)', 'Vendor': 'var(--mint)',
    'छात्र': 'var(--coral)', 'बैचलर': 'var(--amber)', 'वेंडर': 'var(--mint)',
    'विद्यार्थी': 'var(--coral)', 'बॅचलर': 'var(--amber)', 'विक्रेता': 'var(--mint)',
  };
  const avColors = ['var(--coral)', 'var(--mint)', 'var(--amber)', 'var(--violet)', '#FF7043', '#29B6F6'];

  return (
    <section id="reviews" className="testi-section">
      <div className="testi-inner">
        <span ref={r1} className="reveal section-tag tag-purple">{t.testiTag}</span>
        <h2 ref={r1} className="reveal section-heading" style={{ whiteSpace: 'pre-line' }}>{t.testiHeading}</h2>
        <div ref={r2} className="reveal testi-grid">
          {t.testis.map((te, i) => {
            const tc = typeColors[te.type] || 'var(--coral)';
            return (
              <div key={i} className="testi-card"
                onMouseEnter={e => { e.currentTarget.style.borderColor = tc; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; }}>
                <div className="testi-type-badge" style={{ background: `${tc}14`, color: tc }}>{te.type}</div>
                <div className="testi-stars">{'★'.repeat(te.stars)}</div>
                <p className="testi-text">{te.text}</p>
                <div className="testi-author">
                  <div className="testi-av" style={{ background: avColors[i] }}>{te.name[0]}</div>
                  <div>
                    <div className="testi-name">{te.name}</div>
                    <div className="testi-role">{te.role}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────
   DOWNLOAD
───────────────────────────────────────── */
function Download({ t }) {
  const r1 = useReveal(), r2 = useReveal();
  return (
    <section id="download" className="download-section">
      <div className="dl-orb-1" />
      <div className="dl-orb-2" />
      <div className="dl-content">
        <div ref={r1} className="reveal">
          <span className="section-tag tag-coral">{t.dlTag}</span>
          <h2 className="dl-heading">{t.dlHeading}</h2>
          <p className="dl-desc">{t.dlDesc}</p>
        </div>
        <div ref={r2} className="reveal dl-btns">
          <a href="https://play.google.com/store" target="_blank" rel="noreferrer" className="dl-btn">
            <span className="dl-btn-icon">▶</span>
            <div>
              <span className="dl-btn-sub">Get it on</span>
              <span className="dl-btn-name">Google Play</span>
            </div>
          </a>
          <div className="dl-btn disabled">
            <span className="dl-btn-icon">🍎</span>
            <div>
              <span className="dl-btn-sub">{t.dlNote.split('·')[1]?.trim() || 'Coming soon'}</span>
              <span className="dl-btn-name">App Store</span>
            </div>
          </div>
        </div>
        <p className="dl-note">{t.dlNote}</p>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────
   FOOTER
───────────────────────────────────────── */
function Footer({ t }) {
  return (
    <footer className="footer">
      <div className="footer-inner">
        <div className="footer-top">
          <div>
            <div className="footer-logo">
              <div className="footer-logo-mark">🍽</div>
              <div className="footer-logo-text">Swaad<span>India</span></div>
            </div>
            <p className="footer-tagline">{t.footerTagline}</p>
            <div className="footer-socials">
              {['📘', '📸', '🐦'].map((ic, i) => <div key={i} className="social-btn">{ic}</div>)}
            </div>
          </div>
          {t.footerCols.map((col, ci) => (
            <div key={ci}>
              <div className="footer-col-title">{col.title}</div>
              <ul className="footer-links">
                {col.links.map((l, li) => <li key={li}>{l}</li>)}
              </ul>
            </div>
          ))}
        </div>
        <div className="footer-bottom">
          <div className="footer-copy">{t.footerCopy}</div>
          <div className="footer-cities">{t.footerCities}</div>
        </div>
      </div>
    </footer>
  );
}

/* ─────────────────────────────────────────
   ROOT APP
───────────────────────────────────────── */
export default function App() {
  const [lang, setLang] = useState('en');
  const t = T[lang];
  return (
    <div>
      <Navbar lang={lang} setLang={setLang} t={t} />
      <Hero t={t} />
      <Marquee />
      <Problem t={t} />
      <Features t={t} />
      <QRSection t={t} />
      <Stats t={t} />
      <HowItWorks t={t} />
      <Testimonials t={t} />
      <Download t={t} />
      <Footer t={t} />
    </div>
  );
}